import type { ActivityEntry } from "./types";

/**
 * How the Activity timeline orders its entries.
 *
 * Two clocks feed the timeline and they do not agree. The lifecycle events
 * (assigned, claimed, completed) carry only the worker's server-side stamp. A
 * pick event carries two: `occurred_at`, the device's own clock, and
 * `processed_at`, the worker's stamp when it handled that event. A device clock
 * runs ahead or behind (on the task this was found on, about two seconds
 * ahead), and sorting a device time against a server time put a pick that
 * happened before "Task completed" underneath it.
 *
 * So ordering uses the server clock throughout - a pick's `processed_at` -
 * and the device time is left for display only (`occurredAt`). Where a pick has
 * no `processed_at`, its device time stands in.
 *
 * Equal instants are real, not rare: the worker stamps a task's completion with
 * the same instant as the final pick's `processed_at`. They break by where the
 * entry sits in the task's life, never by the order the service happened to
 * return them in, which is not guaranteed.
 */

/** Position in a task's life; the tie-break when two entries share an instant. */
const LIFECYCLE_RANK: Record<ActivityEntry["kind"], number> = {
  activated: 0,
  assigned: 1,
  started: 2,
  pick: 3,
  completed: 4,
};

/** An unparseable time sorts last rather than poisoning the comparison with NaN. */
function ms(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

/** The instant the entry is ordered by - the server's clock. */
function orderedAt(entry: ActivityEntry): number {
  return entry.kind === "pick"
    ? ms(entry.processedAt ?? entry.occurredAt)
    : ms(entry.timestamp);
}

/** The instant the entry is shown at - the device's clock for a pick. */
function shownAt(entry: ActivityEntry): number {
  return entry.kind === "pick" ? ms(entry.occurredAt) : ms(entry.timestamp);
}

/**
 * Orders entries oldest first: server instant, then position in the task's
 * life, then the time shown. Zero only for entries that are interchangeable, so
 * a stable sort keeps their input order.
 */
export function compareActivityEntries(
  a: ActivityEntry,
  b: ActivityEntry,
): number {
  return (
    orderedAt(a) - orderedAt(b) ||
    LIFECYCLE_RANK[a.kind] - LIFECYCLE_RANK[b.kind] ||
    shownAt(a) - shownAt(b)
  );
}
